import { useState, useMemo } from 'react';
import { X, AlertTriangle, CheckCircle, Send, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';

interface CompleteMissingInfoModalProps {
    appointment: {
        id: string;
        agentDecisionReason?: Array<{ check: string; passed: boolean; detail: string }>;
        driverName?: string;
        truckNumber?: string;
        trailerNumber?: string;
        trailerType?: string;
        estimatedPalletCount?: number;
        carrierNotes?: string;
        customerPoNumber?: string;
        warehouseName?: string;
    };
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

// Map from agent engine detail phrases to form field keys
// These MUST match the exact phrases used in agentEngine.ts Check 1
const FIELD_PHRASE_MAP: Array<[string, string]> = [
    ['driver name', 'driverName'],
    ['truck number', 'truckNumber'],
    ['trailer number', 'trailerNumber'],
    ['trailer type', 'trailerType'],
    ['estimated pallet count', 'estimatedPalletCount'],
    ['carrier notes', 'carrierNotes'],
];

const TRAILER_TYPES = ['Dry Van', 'Reefer', 'Flatbed', 'Step Deck', 'Other'];

function parseMissingFields(reasons?: Array<{ check: string; passed: boolean; detail: string }>): string[] {
    if (!reasons || reasons.length === 0) return [];

    const missingFields = new Set<string>();

    for (const reason of reasons) {
        if (reason.check === 'required_info' && !reason.passed) {
            const detailLower = reason.detail.toLowerCase();
            for (const [phrase, fieldKey] of FIELD_PHRASE_MAP) {
                if (detailLower.includes(phrase)) {
                    missingFields.add(fieldKey);
                }
            }
        }
    }

    return Array.from(missingFields);
}


export function CompleteMissingInfoModal({ appointment, open, onClose, onSuccess }: CompleteMissingInfoModalProps) {
    const missingFields = useMemo(
        () => parseMissingFields(appointment.agentDecisionReason),
        [appointment.agentDecisionReason]
    );

    const [driverName, setDriverName] = useState(appointment.driverName || '');
    const [truckNumber, setTruckNumber] = useState(appointment.truckNumber || '');
    const [trailerNumber, setTrailerNumber] = useState(appointment.trailerNumber || '');
    const [trailerType, setTrailerType] = useState(appointment.trailerType || '');
    const [estimatedPalletCount, setEstimatedPalletCount] = useState(
        appointment.estimatedPalletCount?.toString() || ''
    );
    const [carrierNotes, setCarrierNotes] = useState(appointment.carrierNotes || '');
    const [submitting, setSubmitting] = useState(false);

    if (!open) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const data: Record<string, any> = {};
            if (missingFields.includes('driverName')) data.driverName = driverName;
            if (missingFields.includes('truckNumber')) data.truckNumber = truckNumber;
            if (missingFields.includes('trailerNumber')) data.trailerNumber = trailerNumber;
            if (missingFields.includes('trailerType')) data.trailerType = trailerType;
            if (missingFields.includes('estimatedPalletCount')) {
                data.estimatedPalletCount = parseInt(estimatedPalletCount, 10) || undefined;
            }
            if (missingFields.includes('carrierNotes')) data.carrierNotes = carrierNotes;

            const result = await api.updateCarrierAppointment(appointment.id, data) as any;

            if (result?.status === 'approved') {
                toast.success('✅ Your appointment has been approved!');
            } else {
                toast('Your information has been updated and is under review.', {
                    icon: 'ℹ️',
                    style: { background: '#1e40af', color: '#fff' },
                });
            }

            onSuccess();
        } catch (err: any) {
            toast.error(err.message || 'Failed to update appointment');
        } finally {
            setSubmitting(false);
        }
    };

    const showField = (field: string) => missingFields.includes(field);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

            {/* Modal */}
            <div className="relative w-full max-w-lg bg-card border border-border rounded-xl shadow-2xl z-10 max-h-[90vh] overflow-y-auto">
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    aria-label="Close"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Header */}
                <div className="p-6 pb-4 border-b border-border">
                    <h2 className="text-xl font-bold text-foreground tracking-tight pr-8">
                        Complete Your Appointment Request
                    </h2>
                    <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                        {appointment.customerPoNumber && (
                            <span className="font-semibold text-foreground">PO #{appointment.customerPoNumber}</span>
                        )}
                        {appointment.warehouseName && (
                            <span>• {appointment.warehouseName}</span>
                        )}
                    </div>
                </div>

                {/* Info Banner */}
                <div className="mx-6 mt-4 p-3 bg-amber-50/80 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/40 rounded-lg flex items-start gap-2.5">
                    <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                        The following information is needed to process your request.
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {showField('driverName') && (
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Driver Name</label>
                            <input
                                type="text"
                                value={driverName}
                                onChange={(e) => setDriverName(e.target.value)}
                                placeholder="Full name of the driver"
                                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                required
                            />
                        </div>
                    )}

                    {showField('truckNumber') && (
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Truck Number</label>
                            <input
                                type="text"
                                value={truckNumber}
                                onChange={(e) => setTruckNumber(e.target.value)}
                                placeholder="e.g. TRK-1234"
                                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                required
                            />
                        </div>
                    )}

                    {showField('trailerNumber') && (
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Trailer Number</label>
                            <input
                                type="text"
                                value={trailerNumber}
                                onChange={(e) => setTrailerNumber(e.target.value)}
                                placeholder="e.g. TRL-5678"
                                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                required
                            />
                        </div>
                    )}

                    {showField('trailerType') && (
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Trailer Type</label>
                            <select
                                value={trailerType}
                                onChange={(e) => setTrailerType(e.target.value)}
                                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                required
                            >
                                <option value="">Select trailer type</option>
                                {TRAILER_TYPES.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {showField('estimatedPalletCount') && (
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Estimated Pallet Count</label>
                            <input
                                type="number"
                                min="1"
                                max="200"
                                value={estimatedPalletCount}
                                onChange={(e) => setEstimatedPalletCount(e.target.value)}
                                placeholder="1-200"
                                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                required
                            />
                        </div>
                    )}

                    {showField('carrierNotes') && (
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Carrier Notes</label>
                            <textarea
                                value={carrierNotes}
                                onChange={(e) => setCarrierNotes(e.target.value.slice(0, 500))}
                                placeholder="Any additional notes for the warehouse..."
                                maxLength={500}
                                rows={3}
                                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-none"
                            />
                            <span className="text-[10px] text-muted-foreground">{carrierNotes.length}/500</span>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-outline flex-1"
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary flex-1 shadow-lg shadow-primary/20 gap-2"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Submitting…
                                </>
                            ) : (
                                <>
                                    <Send className="w-4 h-4" />
                                    Update & Resubmit
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
